/**
 * Telling cars apart, for any make.
 *
 * The VIN settles it when the car reports one, but plenty of cars do not
 * answer the request for it. Every OBD-II car does answer which mode 01 PIDs
 * it supports, though, and that list differs between engines, ECU generations
 * and model years. Together with the bus protocol and the declared fuel type
 * it is a usable fingerprint: good at telling a diesel Golf from a petrol
 * Clio, unable to tell two identical cars apart. Verdicts drawn from it say
 * "probably" for that reason.
 */
import { decodeVin, normalizeVin, shortVin } from './vin.js';

export interface VehicleIdentity {
  /** When the car reports one. */
  vin?: string;
  /**
   * Mode 01 support bitmaps, keyed by their base PID in two hex digits
   * ("00", "20", ...), merged over every ECU that answered.
   */
  pidMaps?: Record<string, number>;
  /** OBD protocol number the adapter settled on (ELM327 ATDPN). */
  protocol?: number;
  /** PID 0x51, when the car declares its fuel type. */
  fuelTypeCode?: number;
}

export type IdentityVerdict = 'same' | 'different' | 'probably-same' | 'probably-different' | 'unknown';

export interface IdentityMatch {
  verdict: IdentityVerdict;
  /** One sentence saying why, for the driver. */
  reason: string;
}

/** Whether a verdict should be shown as a warning. */
export const isMismatch = (m: IdentityMatch | undefined): boolean =>
  m?.verdict === 'different' || m?.verdict === 'probably-different';

export const BITMAP_BASES = ['00', '20', '40', '60', '80', 'A0', 'C0'] as const;

/** How much of the supported-PID list must agree to count as the same kind of car. */
const SAME_PID_SHARE = 0.9;

export function compareIdentities(a: VehicleIdentity | undefined, b: VehicleIdentity | undefined): IdentityMatch {
  if (!a || !b) return { verdict: 'unknown', reason: 'There is nothing recorded to compare with.' };

  if (a.vin && b.vin) {
    return a.vin === b.vin
      ? { verdict: 'same', reason: 'The VIN matches (' + shortVin(a.vin) + ').' }
      : { verdict: 'different', reason: 'The VINs differ (' + shortVin(a.vin) + ' and ' + shortVin(b.vin) + ').' };
  }

  if (a.protocol && b.protocol && a.protocol !== b.protocol) {
    return { verdict: 'probably-different', reason: 'The cars talk a different OBD protocol.' };
  }
  if (a.fuelTypeCode && b.fuelTypeCode && a.fuelTypeCode !== b.fuelTypeCode) {
    return { verdict: 'probably-different', reason: 'The cars declare different fuel types.' };
  }

  // Only bitmaps both sides managed to read are compared: an interrupted
  // discovery leaves later ones out, and that is not a different car.
  const shared = BITMAP_BASES.filter((k) => a.pidMaps?.[k] !== undefined && b.pidMaps?.[k] !== undefined);
  if (shared.length === 0) {
    return { verdict: 'unknown', reason: 'Neither a VIN nor a sensor list is available for both.' };
  }
  let both = 0;
  let either = 0;
  for (const k of shared) {
    const x = (a.pidMaps?.[k] ?? 0) >>> 0;
    const y = (b.pidMaps?.[k] ?? 0) >>> 0;
    both += popcount(x & y);
    either += popcount(x | y);
  }
  const share = either === 0 ? 1 : both / either;
  return share >= SAME_PID_SHARE
    ? {
      verdict: 'probably-same',
      reason: 'The engine computer reports the same sensors. Without a VIN, two cars of the same model look alike.',
    }
    : {
      verdict: 'probably-different',
      reason: 'The engine computer reports different sensors (' + Math.round(share * 100) + '% in common).',
    };
}

/** A short label, e.g. "VIN …A12345 · Renault, France" or "no VIN · 41 PIDs · CAN 11-bit 500 kbit/s". */
export function describeIdentity(id: VehicleIdentity | undefined): string {
  if (!id) return 'unknown car';
  if (id.vin) {
    const info = decodeVin(id.vin);
    const where = [info?.manufacturer, info?.region].filter(Boolean).join(', ');
    return 'VIN ' + shortVin(id.vin) + (where ? ' · ' + where : '');
  }
  const parts = ['no VIN'];
  const pids = supportedPidCount(id);
  if (pids > 0) parts.push(pids + ' PIDs');
  const protocol = id.protocol ? PROTOCOL_NAMES[id.protocol] : undefined;
  if (protocol) parts.push(protocol);
  return parts.join(' · ');
}

export function supportedPidCount(id: VehicleIdentity): number {
  let n = 0;
  for (const k of BITMAP_BASES) {
    const map = id.pidMaps?.[k];
    // Bit 0 only says whether the next bitmap exists.
    if (map !== undefined) n += popcount((map >>> 0) & ~1);
  }
  return n;
}

/**
 * Clean an identity from storage or a file. Returns undefined when nothing
 * usable is left.
 */
export function sanitizeIdentity(raw: unknown): VehicleIdentity | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const id: VehicleIdentity = {};
  const vin = normalizeVin(r.vin);
  if (vin) id.vin = vin;
  if (typeof r.pidMaps === 'object' && r.pidMaps !== null && !Array.isArray(r.pidMaps)) {
    const maps: Record<string, number> = {};
    const src = r.pidMaps as Record<string, unknown>;
    for (const k of BITMAP_BASES) {
      const v = src[k];
      if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 0xffffffff) maps[k] = v;
    }
    if (Object.keys(maps).length > 0) id.pidMaps = maps;
  }
  if (isInt(r.protocol, 1, 12)) id.protocol = r.protocol;
  if (isInt(r.fuelTypeCode, 1, 255)) id.fuelTypeCode = r.fuelTypeCode;
  return Object.keys(id).length > 0 ? id : undefined;
}

/**
 * A short, stable key for filing this car's records under.
 *
 * The VIN when there is one. Otherwise the fingerprint, which is exact where
 * `compareIdentities` is tolerant -- so a caller that keeps records should
 * match against known cars first and only fall back to this for a new one.
 */
export function carKey(id: VehicleIdentity | undefined): string {
  if (!id) return 'unknown';
  if (id.vin) return 'vin:' + id.vin;
  const maps = BITMAP_BASES.map((k) => ((id.pidMaps?.[k] ?? 0) >>> 0).toString(16)).join('-');
  return 'fp:' + (id.protocol ?? 0) + '.' + (id.fuelTypeCode ?? 0) + '.' + maps;
}

/** A stable key: equal for identities that are the same record. */
export function identityKey(id: VehicleIdentity | undefined): string {
  if (!id) return '';
  return JSON.stringify([id.vin ?? '', BITMAP_BASES.map((k) => id.pidMaps?.[k] ?? null), id.protocol ?? 0, id.fuelTypeCode ?? 0]);
}

/** ELM327 protocol numbers. */
export const PROTOCOL_NAMES: Record<number, string> = {
  1: 'SAE J1850 PWM',
  2: 'SAE J1850 VPW',
  3: 'ISO 9141-2',
  4: 'ISO 14230-4 KWP (slow init)',
  5: 'ISO 14230-4 KWP (fast init)',
  6: 'CAN 11-bit 500 kbit/s',
  7: 'CAN 29-bit 500 kbit/s',
  8: 'CAN 11-bit 250 kbit/s',
  9: 'CAN 29-bit 250 kbit/s',
  10: 'SAE J1939 CAN',
  11: 'CAN (user 1)',
  12: 'CAN (user 2)',
};

function popcount(v: number): number {
  let x = v >>> 0;
  let n = 0;
  while (x) {
    x = (x & (x - 1)) >>> 0;
    n++;
  }
  return n;
}

const isInt = (v: unknown, min: number, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
